/* eslint-disable react-refresh/only-export-components */
import {
  DimeEmpty,
  DimeEmptyHeader,
  DimeEmptyMedia,
  DimeEmptyTitle,
  DimeEmptyDescription,
  DimeEmptyContent,
  DimeButton,
  DimeInputGroup,
  DimeInputGroupAddon,
  DimeInputGroupInput,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Inbox, Search, SearchX } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/empty')({
  component: EmptyDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="rounded-lg border border-border bg-background p-6">{children}</div>;
}

function EmptyDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Empty</h1>
      <p className="mb-8 text-muted-foreground">
        A centered placeholder for blank lists, empty search results, or first-run states. Compose
        media, title, description, and an optional action.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeEmpty>
            <DimeEmptyHeader>
              <DimeEmptyMedia variant="icon">
                <Inbox />
              </DimeEmptyMedia>
              <DimeEmptyTitle>No parts yet</DimeEmptyTitle>
              <DimeEmptyDescription>Create your first part to get started.</DimeEmptyDescription>
            </DimeEmptyHeader>
            <DimeEmptyContent>
              <DimeButton>New part</DimeButton>
            </DimeEmptyContent>
          </DimeEmpty>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Put the primary call-to-action inside <code className="font-mono">DimeEmptyContent</code>.
        </p>
      </DocsSection>

      <DocsSection title="No results">
        <Preview>
          <DimeEmpty>
            <DimeEmptyHeader>
              <DimeEmptyMedia variant="icon">
                <SearchX />
              </DimeEmptyMedia>
              <DimeEmptyTitle>No matching parts</DimeEmptyTitle>
              <DimeEmptyDescription>
                Try adjusting your filters or search terms.
              </DimeEmptyDescription>
            </DimeEmptyHeader>
          </DimeEmpty>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The action slot is optional — omit <code className="font-mono">DimeEmptyContent</code> for
          informational states.
        </p>
      </DocsSection>

      <DocsSection title="Outline">
        <Preview>
          <DimeEmpty className="rounded-lg border">
            <DimeEmptyHeader>
              <DimeEmptyMedia variant="icon">
                <Inbox />
              </DimeEmptyMedia>
              <DimeEmptyTitle>No notifications</DimeEmptyTitle>
              <DimeEmptyDescription>You're all caught up.</DimeEmptyDescription>
            </DimeEmptyHeader>
            <DimeEmptyContent>
              <DimeButton variant="outline">Refresh</DimeButton>
            </DimeEmptyContent>
          </DimeEmpty>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add a <code className="font-mono">border</code> to set the empty state apart inside a
          panel or card.
        </p>
      </DocsSection>

      <DocsSection title="Background">
        <Preview>
          <DimeEmpty className="rounded-lg bg-muted/50">
            <DimeEmptyHeader>
              <DimeEmptyMedia variant="icon">
                <Inbox />
              </DimeEmptyMedia>
              <DimeEmptyTitle>Inbox zero</DimeEmptyTitle>
              <DimeEmptyDescription>Nothing needs your attention right now.</DimeEmptyDescription>
            </DimeEmptyHeader>
          </DimeEmpty>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Apply a muted background (<code className="font-mono">bg-muted/50</code>) for a softer,
          filled treatment.
        </p>
      </DocsSection>

      <DocsSection title="With input group">
        <Preview>
          <DimeEmpty className="rounded-lg border">
            <DimeEmptyHeader>
              <DimeEmptyMedia variant="icon">
                <SearchX />
              </DimeEmptyMedia>
              <DimeEmptyTitle>404 — Not found</DimeEmptyTitle>
              <DimeEmptyDescription>
                The page you're looking for doesn't exist. Try a search instead.
              </DimeEmptyDescription>
            </DimeEmptyHeader>
            <DimeEmptyContent>
              <DimeInputGroup className="w-full max-w-xs">
                <DimeInputGroupAddon>
                  <Search />
                </DimeInputGroupAddon>
                <DimeInputGroupInput placeholder="Search the docs..." />
              </DimeInputGroup>
            </DimeEmptyContent>
          </DimeEmpty>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The content slot accepts any control — here a{' '}
          <code className="font-mono">DimeInputGroup</code> turns the empty state into a recovery
          action.
        </p>
      </DocsSection>
    </div>
  );
}
